import { useState, useEffect, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Users, TrendingUp, Activity, Copy, Download, FileText, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import API from "../../lib/api";
import { PERSONA_DEFINITIONS } from "../../config/surveyConfig";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const CHART_COLORS = ["#a21caf", "#f59e0b", "#22c55e", "#3b82f6", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6"];

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="nb-card-sm flex items-start gap-3" data-testid={testId}>
      <div className="w-9 h-9 rounded-lg bg-[#f5d0fe] border-2 border-[#111] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(17,17,17,1)] flex-shrink-0">
        <Icon className="w-4 h-4 text-[#a21caf]" strokeWidth={2.5} />
      </div>
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-[#111] font-parkinsans">{value}</p>
        {sub && <p className="text-xs text-gray-400">{sub}</p>}
      </div>
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <div className="nb-card" data-testid={testId}>
      <h3 className="font-parkinsans text-lg mb-4">{title}</h3>
      {children}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border-2 border-[#111] rounded-xl p-3 shadow-[4px_4px_0px_0px_rgba(17,17,17,1)]">
        <p className="font-semibold text-sm">{label || payload[0]?.name}</p>
        <p className="text-sm text-[#a21caf] font-bold">{payload[0]?.value}</p>
      </div>
    );
  }
  return null;
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openTexts, setOpenTexts] = useState({ concerns: false, hopes: false, questions: false, learn: false });

  useEffect(() => {
    API.get("/admin/stats").then(r => { setStats(r.data); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, []);

  const insights = useMemo(() => {
    if (!stats || stats.total_responses === 0) return [];
    const lines = [];
    const topPersona = stats.persona_distribution?.sort((a, b) => b.count - a.count)[0];
    if (topPersona) lines.push(`Most respondents are "${topPersona.name}" (${topPersona.count} out of ${stats.total_responses}).`);
    if (stats.ai_regular_pct > 0) lines.push(`${stats.ai_regular_pct}% of respondents currently use AI regularly or occasionally.`);
    if (stats.top_concerns?.length) lines.push(`Top concerns: ${stats.top_concerns.slice(0, 3).join(", ")}.`);
    if (stats.top_learning_goals?.length) lines.push(`Most requested learning topics: ${stats.top_learning_goals.slice(0, 3).join(", ")}.`);
    lines.push(`Average readiness score is ${stats.average_score} out of 36.`);
    return lines;
  }, [stats]);

  const handleCopySummary = () => {
    const text = insights.join("\n");
    navigator.clipboard.writeText(text);
    toast.success("Summary copied to clipboard!");
  };

  const handleExportPDF = () => {
    if (!stats) return;
    const doc = new jsPDF();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("AI Readiness Pulse - Summary Report", 14, 20);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);
    doc.text(`Total Responses: ${stats.total_responses}`, 14, 35);
    doc.text(`Average Score: ${stats.average_score} / 36`, 14, 42);
    doc.text(`AI Users (Regular/Occasional): ${stats.ai_regular_pct}%`, 14, 49);

    doc.setFontSize(14);
    doc.text("Key Insights", 14, 62);
    doc.setFontSize(10);
    insights.forEach((line, i) => {
      doc.text(`- ${line}`, 18, 70 + i * 7);
    });

    let y = 70 + insights.length * 7 + 10;
    if (stats.persona_distribution?.length) {
      doc.setFontSize(14);
      doc.text("Persona Distribution", 14, y);
      y += 5;
      autoTable(doc, {
        startY: y,
        head: [["Persona", "Count"]],
        body: stats.persona_distribution.map(p => [p.name, p.count]),
        theme: "grid",
        headStyles: { fillColor: [162, 28, 175] }
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    if (stats.concern_counts?.length) {
      doc.setFontSize(14);
      doc.text("Top Concerns", 14, y);
      y += 5;
      autoTable(doc, {
        startY: y,
        head: [["Concern", "Count"]],
        body: stats.concern_counts.slice(0, 10).map(c => [c.name, c.count]),
        theme: "grid",
        headStyles: { fillColor: [162, 28, 175] }
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    if (stats.learning_goal_counts?.length) {
      doc.setFontSize(14);
      doc.text("Learning Goals", 14, y);
      y += 5;
      autoTable(doc, {
        startY: y,
        head: [["Goal", "Count"]],
        body: stats.learning_goal_counts.slice(0, 10).map(g => [g.name, g.count]),
        theme: "grid",
        headStyles: { fillColor: [162, 28, 175] }
      });
    }

    doc.save("ai-readiness-pulse-report.pdf");
    toast.success("PDF report downloaded!");
  };

  const handleExportCSV = async () => {
    try {
      const response = await API.get("/admin/export/csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "ai_readiness_responses.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV exported!");
    } catch {
      toast.error("Failed to export CSV");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-[#a21caf] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!stats || stats.total_responses === 0) {
    return (
      <div className="nb-card text-center py-12" data-testid="empty-dashboard">
        <div className="text-5xl mb-4">📊</div>
        <h2 className="font-parkinsans text-2xl mb-2">No responses yet</h2>
        <p className="text-gray-500 mb-4">Share the survey link with your team, or seed demo data from Settings.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header + actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="font-parkinsans text-2xl sm:text-3xl">Dashboard</h1>
        <div className="flex gap-2 flex-wrap">
          <button onClick={handleCopySummary} className="nb-btn-secondary text-sm py-2 px-4" data-testid="copy-summary-btn">
            <Copy className="w-4 h-4" strokeWidth={2.5} /> Copy Summary
          </button>
          <button onClick={handleExportPDF} className="nb-btn-secondary text-sm py-2 px-4" data-testid="export-pdf-btn">
            <FileText className="w-4 h-4" strokeWidth={2.5} /> PDF
          </button>
          <button onClick={handleExportCSV} className="nb-btn-primary text-sm py-2 px-4" data-testid="export-csv-btn">
            <Download className="w-4 h-4" strokeWidth={2.5} /> CSV
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Completed" value={stats.total_responses}
          sub={stats.total_started > 0 ? `${stats.completion_rate}% completion rate` : null}
          testId="stat-total" />
        <StatCard icon={TrendingUp} label="Avg Score" value={`${stats.average_score}/36`} testId="stat-avg-score" />
        <StatCard icon={Activity} label="AI Users" value={`${stats.ai_regular_pct}%`} sub="Regular or occasional" testId="stat-ai-users" />
        <StatCard icon={Users} label="Started" value={stats.total_started || 0} sub="with email submitted" testId="stat-started" />
      </div>

      {/* Insights panel */}
      <div className="nb-card bg-[#f5d0fe]/20" data-testid="insights-panel">
        <h3 className="font-parkinsans text-lg mb-3">Key Insights</h3>
        <ul className="space-y-2">
          {insights.map((line, i) => (
            <li key={i} className="text-sm text-[#111] flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#a21caf] mt-1.5 flex-shrink-0" />
              {line}
            </li>
          ))}
        </ul>
      </div>

      {/* Charts row 1 */}
      <div className="grid md:grid-cols-2 gap-6">
        <ChartCard title="Persona Distribution" testId="chart-persona">
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={stats.persona_distribution} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90}
                stroke="#111" strokeWidth={2} label={({ name, count }) => `${name} (${count})`}>
                {stats.persona_distribution.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Score Distribution" testId="chart-score">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={stats.score_distribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="range" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="#a21caf" stroke="#111" strokeWidth={2} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid md:grid-cols-2 gap-6">
        <ChartCard title="AI Usage Level" testId="chart-usage">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={stats.usage_distribution} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="#f59e0b" stroke="#111" strokeWidth={2} radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Self-Rating Distribution" testId="chart-self-rating">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={stats.self_rating_distribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="#22c55e" stroke="#111" strokeWidth={2} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 3 */}
      <div className="grid md:grid-cols-2 gap-6">
        <ChartCard title="Top Concerns" testId="chart-concerns">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.concern_counts?.slice(0, 8)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="#ef4444" stroke="#111" strokeWidth={2} radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Learning Goals" testId="chart-learning">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.learning_goal_counts?.slice(0, 8)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="#3b82f6" stroke="#111" strokeWidth={2} radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Usage frequency + context */}
      {(stats.frequency_distribution?.length > 0 || stats.context_distribution?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-6">
          {stats.frequency_distribution?.length > 0 && (
            <ChartCard title="Usage Frequency" testId="chart-frequency">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.frequency_distribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#8b5cf6" stroke="#111" strokeWidth={2} radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
          {stats.context_distribution?.length > 0 && (
            <ChartCard title="Work vs Personal Use" testId="chart-context">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={stats.context_distribution} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={70}
                    stroke="#111" strokeWidth={2} label={({ name, count }) => `${name} (${count})`}>
                    {stats.context_distribution.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}

      {/* Open text responses */}
      {Object.entries({
        concerns: { title: "Open Concerns", data: stats.open_texts?.concerns },
        hopes: { title: "Hopes & Wishes", data: stats.open_texts?.hopes },
        questions: { title: "Questions for Trainers", data: stats.open_texts?.questions },
        learn: { title: "Additional Learning Interests", data: stats.open_texts?.learn }
      }).map(([key, { title, data }]) => data?.length > 0 && (
        <div key={key} className="nb-card" data-testid={`open-text-${key}`}>
          <button
            onClick={() => setOpenTexts(p => ({ ...p, [key]: !p[key] }))}
            className="w-full flex items-center justify-between"
          >
            <h3 className="font-parkinsans text-lg">{title} ({data.length})</h3>
            {openTexts[key] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          {openTexts[key] && (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {data.map((item, i) => (
                <div key={i} className="nb-card-sm bg-[#fdf7ff]">
                  <p className="text-xs font-bold text-[#a21caf] mb-1">{item.name}</p>
                  <p className="text-sm text-[#111]">{item.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
